import { useState, useRef } from 'react'
import { Upload, X, File, AlertCircle, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'

import { getErrorMessage } from '../utils/error.utils'
interface FileUploadProps {
  onUpload: (files: FileList) => Promise<void>
  multiple?: boolean
  accept?: string
  maxSize?: number // in MB
  maxFiles?: number
  disabled?: boolean
  className?: string
}

interface UploadFile {
  id: string
  file: File
  status: 'pending' | 'uploading' | 'success' | 'error'
  error?: string
}

export function FileUpload({
  onUpload,
  multiple = false,
  accept = "*/*",
  maxSize = 10, // 10MB default
  maxFiles = 5,
  disabled = false,
  className = ""
}: FileUploadProps) {
  const [files, setFiles] = useState<UploadFile[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `Arquivo muito grande. Máximo: ${maxSize}MB`
    }

    // Check file type if accept is specified
    if (accept !== "*/*") {
      const acceptedTypes = accept.split(',').map(type => type.trim())
      const fileType = file.type
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
      
      const isAccepted = acceptedTypes.some(acceptedType => {
        if (acceptedType.startsWith('.')) {
          return fileExtension === acceptedType.toLowerCase()
        }
        return fileType.match(acceptedType.replace('*', '.*'))
      })

      if (!isAccepted) {
        return 'Tipo de arquivo não permitido'
      }
    }

    return null
  }

  const processFiles = (fileList: FileList) => {
    const newFiles: UploadFile[] = []

    Array.from(fileList).forEach((file, index) => {
      if (!multiple && files.length + newFiles.length >= 1) {
        return
      }

      if (files.length + newFiles.length >= maxFiles) {
        return
      }

      const error = validateFile(file)
      const uploadFile: UploadFile = {
        id: `${Date.now()}-${index}`,
        file: file, // Keep the original File object
        status: error ? 'error' as const : 'pending' as const,
        error: error ?? undefined
      }

      newFiles.push(uploadFile)
    })

    setFiles(prev => [...prev, ...newFiles])
    return newFiles.filter(file => file.status === 'pending')
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    
    const validFiles = processFiles(e.target.files)
    if (validFiles.length > 0) {
      handleUpload(validFiles)
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    
    if (disabled || isUploading) return

    const validFiles = processFiles(e.dataTransfer.files)
    if (validFiles.length > 0) {
      handleUpload(validFiles)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!disabled && !isUploading) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleUpload = async (filesToUpload: UploadFile[]) => {
    setIsUploading(true)

    // Update files to uploading status
    setFiles(prev => prev.map(file =>
      filesToUpload.some(f => f.id === file.id)
        ? { ...file, status: 'uploading' as const }
        : file
    ))

    try {
      // Extract original File objects
      const originalFiles = filesToUpload.map(f => f.file)

      // Create a real FileList using DataTransfer API
      const dataTransfer = new DataTransfer()
      originalFiles.forEach(file => dataTransfer.items.add(file))
      const fileList = dataTransfer.files

      await onUpload(fileList)

      // Mark files as successful
      setFiles(prev => prev.map(file =>
        filesToUpload.some(f => f.id === file.id)
          ? { ...file, status: 'success' as const }
          : file
      ))

    } catch (error) {
      // Mark files as error
      setFiles(prev => prev.map(file =>
        filesToUpload.some(f => f.id === file.id)
          ? {
              ...file,
              status: 'error' as const,
              error: error instanceof Error ? getErrorMessage(error) : 'Erro no upload'
            }
          : file
      ))
    } finally {
      setIsUploading(false)
    }
  }

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(file => file.id !== fileId))
  }

  const clearAll = () => {
    setFiles([])
  }

  const getStatusIcon = (status: UploadFile['status']) => {
    switch (status) {
      case 'uploading':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" aria-hidden="true" />
      case 'success':
        return <CheckCircle className="h-4 w-4 text-primary" />
      case 'error':
        return <AlertCircle className="h-4 w-4 text-destructive" />
      default:
        return <File className="h-4 w-4 text-muted-foreground" />
    }
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging
            ? 'border-primary bg-primary/5'
            : disabled
            ? 'border-border bg-muted'
            : 'border-input hover:border-muted-foreground/50'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleFileSelect}
          disabled={disabled || isUploading}
          className="hidden"
        />

        <Upload className={`mx-auto h-12 w-12 mb-4 ${
          disabled ? 'text-muted-foreground/40' : 'text-muted-foreground/70'
        }`} />

        <div className="space-y-2">
          <p className={`text-sm ${disabled ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>
            {isDragging
              ? 'Solte os arquivos aqui'
              : 'Arraste arquivos aqui ou clique para selecionar'
            }
          </p>
          
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isUploading}
          >
            Selecionar Arquivos
          </Button>

          <p className="text-xs text-muted-foreground">
            {multiple ? `Até ${maxFiles} arquivos` : 'Apenas um arquivo'} • 
            Máximo {maxSize}MB cada
          </p>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-foreground">
              Arquivos ({files.length})
            </h4>
            {files.length > 0 && !isUploading && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearAll}
              >
                Limpar Tudo
              </Button>
            )}
          </div>

          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.id}
                className={`flex items-center gap-3 p-3 border rounded-lg ${
                  file.status === 'error' ? 'border-destructive/30 bg-destructive/5' :
                  file.status === 'success' ? 'border-primary/30 bg-primary/5' :
                  'border-border'
                }`}
              >
                {getStatusIcon(file.status)}
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {file.file.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(file.file.size)}
                  </p>
                  {file.error && (
                    <p className="text-xs text-destructive mt-1">
                      {file.error}
                    </p>
                  )}
                </div>

                {file.status !== 'uploading' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeFile(file.id)}
                    className="p-1 h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
